import React from 'react';
import './Confirmation.css';

function Confirmation({
  confirmation,
  selectedBooking,
  searchParams,
  onStartNewSearch,
}) {
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const nights = searchParams.checkIn && searchParams.checkOut
    ? Math.ceil(
        (new Date(searchParams.checkOut) -
          new Date(searchParams.checkIn)) /
          (1000 * 60 * 60 * 24)
      )
    : 1;

  const bookingRef = confirmation?.bookingReference || 'HDK-' + Math.random().toString(36).substr(2, 9).toUpperCase();
  const totalPrice = selectedBooking
    ? (selectedBooking.lowestRate || 0) * nights
    : 0;

  const renderStars = (rating) => {
    const stars = [];
    for (let i = 0; i < 5; i++) {
      stars.push(
        <span key={i} className={i < rating ? 'star-filled' : 'star-empty'}>
          ★
        </span>
      );
    }
    return stars;
  };

  return (
    <div className="confirmation">
      <div className="confirmation-container">
        <div className="success-icon">
          <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <polyline points="22 4 12 14.01 9 11.01" />
          </svg>
        </div>

        <h1>Booking Confirmed!</h1>
        <p className="confirmation-subtitle">Your hotel reservation has been successfully booked.</p>

        <div className="booking-reference">
          <p className="ref-label">Booking Reference</p>
          <p className="ref-number">{bookingRef}</p>
          <button
            className="copy-button"
            onClick={() => {
              navigator.clipboard.writeText(bookingRef);
            }}
            title="Copy to clipboard"
          >
            📋 Copy
          </button>
        </div>

        <div className="confirmation-details">
          <h2>Booking Details</h2>

          {selectedBooking && (
            <div className="detail-section">
              <h3>{selectedBooking.name}</h3>
              <div className="detail-grid">
                <div className="detail-item">
                  <span className="label">Hotel Rating</span>
                  <div className="stars">
                    {renderStars(selectedBooking.starRating)}
                  </div>
                </div>

                <div className="detail-item">
                  <span className="label">Location</span>
                  <span className="value">
                    {selectedBooking.address?.city || selectedBooking.city}{selectedBooking.address?.state || selectedBooking.state ? `, ${selectedBooking.address?.state || selectedBooking.state}` : ''}
                  </span>
                </div>

                <div className="detail-item">
                  <span className="label">Check-in</span>
                  <span className="value highlight-date">
                    {formatDate(searchParams.checkIn)}
                  </span>
                </div>

                <div className="detail-item">
                  <span className="label">Check-out</span>
                  <span className="value highlight-date">
                    {formatDate(searchParams.checkOut)}
                  </span>
                </div>

                <div className="detail-item">
                  <span className="label">Room Type</span>
                  <span className="value">
                    {selectedBooking.rooms?.[0]?.type || 'Standard Room'}
                  </span>
                </div>

                <div className="detail-item">
                  <span className="label">Duration</span>
                  <span className="value">
                    {nights} night{nights !== 1 ? 's' : ''}
                  </span>
                </div>

                <div className="detail-item">
                  <span className="label">Guests</span>
                  <span className="value">{searchParams.guests}</span>
                </div>

                <div className="detail-item">
                  <span className="label">Cancellation Policy</span>
                  <span className="value">
                    {selectedBooking.rooms?.[0]?.cancellationPolicy || 'Standard'}
                  </span>
                </div>
              </div>
            </div>
          )}

          <div className="pricing-summary">
            <div className="price-row">
              <span>Rate per night</span>
              <span>${selectedBooking?.lowestRate?.toFixed(2) || '0.00'}</span>
            </div>
            <div className="price-row">
              <span>Number of nights</span>
              <span>{nights}</span>
            </div>
            <div className="price-row total">
              <span>Total Amount</span>
              <span>${totalPrice.toFixed(2)}</span>
            </div>
          </div>
        </div>

        <div className="next-steps">
          <h2>What's Next?</h2>
          <ol className="steps-list">
            <li>A confirmation email has been sent to your email address</li>
            <li>You will receive hotel contact information in your email</li>
            <li>Arrive at the hotel with your confirmation reference number</li>
            <li>Check-in at the hotel on your arrival date</li>
          </ol>
        </div>

        <div className="confirmation-actions">
          <button className="button-primary" onClick={onStartNewSearch}>
            ✈ Search for Another Hotel
          </button>
          <button
            className="button-secondary"
            onClick={() => window.print()}
            title="Print confirmation"
          >
            🖨 Print Confirmation
          </button>
        </div>

        <div className="confirmation-footer">
          <p className="footer-text">
            For assistance or to modify your booking, please contact our support team.
          </p>
        </div>
      </div>
    </div>
  );
}

export default Confirmation;
